import { listPolicies, createPolicy, updatePolicy, deletePolicy } from "./policiesApi";
import {
  PPPC_TCC_IDENTIFIER,
  PppcChoicesMap,
  PppcIdentifierType,
  PppcPolicySnapshot,
  PppcServiceEntry,
  PppcServiceKey,
  PppcServicesDict,
  SavePppcArgs,
  buildPppcPolicyName,
} from "@/types/pppc";
import { PolicyDetails, PolicyPayload, CreatePolicyRequest } from "@/types/api";
import { assertValidPppcChoices } from "./pppcValidation";
import { recordPppcActivity } from "./pppcActivityLog";
import { getCurrentUserEmail } from "@/lib/auth";

/**
 * Returns true if the policy contains a TCC payload (i.e. is a PPPC policy).
 */
export function isPppcPolicy(policy: PolicyDetails): boolean {
  return (policy.payloads ?? []).some(
    (p) => p.identifier === PPPC_TCC_IDENTIFIER,
  );
}

/**
 * Returns true if the policy is a PPPC policy linked to the given app's bundle ID.
 *
 * Detection convention: policies that contain a TCC payload AND whose name
 * includes the app's bundle ID are treated as that app's PPPC policy.
 */
export function isPppcPolicyForApp(policy: PolicyDetails, bundleId: string): boolean {
  if (!bundleId) return false;
  if (!isPppcPolicy(policy)) return false;
  return (policy.name ?? "").includes(bundleId);
}

function parseTccPayload(payload: PolicyPayload | undefined): PppcServicesDict {
  if (!payload?.configJson) return {};
  try {
    const cfg = JSON.parse(payload.configJson) as { Services?: PppcServicesDict };
    return cfg.Services ?? {};
  } catch {
    return {};
  }
}

function servicesDictToSnapshot(
  services: PppcServicesDict,
  fallbackIdentifier: string,
): {
  choices: PppcChoicesMap;
  codeRequirement: string;
  identifier: string;
  identifierType: PppcIdentifierType;
} {
  const choices: PppcChoicesMap = {};
  let codeRequirement = "";
  let identifier = fallbackIdentifier;
  let identifierType: PppcIdentifierType = "bundleID";

  (Object.keys(services) as PppcServiceKey[]).forEach((key) => {
    const entries = services[key];
    if (!entries || entries.length === 0) return;
    const entry =
      entries.find((e) => e.Identifier === fallbackIdentifier) ?? entries[0];
    choices[key] = entry.Allowed ? "allow" : "deny";
    if (!codeRequirement && entry.CodeRequirement) {
      codeRequirement = entry.CodeRequirement;
    }
    if (entry.Identifier) identifier = entry.Identifier;
    if (entry.IdentifierType === "path" || entry.IdentifierType === "bundleID") {
      identifierType = entry.IdentifierType;
    }
  });

  return { choices, codeRequirement, identifier, identifierType };
}

/**
 * Extract the app name from a PPPC policy name following our naming convention.
 *  "PPPC — Zoom (us.zoom.xos)" → "Zoom"
 */
export function extractAppNameFromPolicyName(name: string): string {
  if (!name) return "";
  const m = name.match(/^PPPC\s+[—-]\s+(.+?)\s+\(([^)]+)\)\s*$/);
  return m ? m[1].trim() : name;
}

function snapshotFromPolicy(
  policy: PolicyDetails,
  fallbackIdentifier: string,
): PppcPolicySnapshot {
  const tccPayload = (policy.payloads ?? []).find(
    (p) => p.identifier === PPPC_TCC_IDENTIFIER,
  );
  const services = parseTccPayload(tccPayload);
  const { choices, codeRequirement, identifier, identifierType } =
    servicesDictToSnapshot(services, fallbackIdentifier);

  return {
    policyId: policy.id,
    codeRequirement,
    identifier,
    identifierType,
    appName: extractAppNameFromPolicyName(policy.name ?? ""),
    choices,
    updatedAt: policy.updatedAt,
    policyName: policy.name,
  };
}

/**
 * Find the PPPC policy linked to the given app, if any.
 */
export async function findPppcPolicyForApp(
  bundleId: string,
): Promise<PppcPolicySnapshot | null> {
  if (!bundleId) return null;
  const policies = await listPolicies();
  const match = policies.find((p) => isPppcPolicyForApp(p, bundleId));
  if (!match) return null;
  return snapshotFromPolicy(match, bundleId);
}

/**
 * List every PPPC policy in the catalog (linked-to-catalog and external).
 */
export async function listPppcPolicies(): Promise<PppcPolicySnapshot[]> {
  const policies = await listPolicies();
  return policies
    .filter(isPppcPolicy)
    .map((p) => snapshotFromPolicy(p, ""));
}

/**
 * Build the Services dict, including only services the admin has set to
 * allow/deny. Skips "unset".
 */
export function buildServicesDict(args: {
  bundleId: string;
  identifierType?: PppcIdentifierType;
  codeRequirement: string;
  choices: PppcChoicesMap;
}): PppcServicesDict {
  const { bundleId, codeRequirement, choices, identifierType = "bundleID" } = args;
  const out: PppcServicesDict = {};
  (Object.keys(choices) as PppcServiceKey[]).forEach((key) => {
    const choice = choices[key];
    if (choice !== "allow" && choice !== "deny") return;
    const entry: PppcServiceEntry = {
      Identifier: bundleId,
      IdentifierType: identifierType,
      CodeRequirement: codeRequirement,
      Allowed: choice === "allow",
    };
    out[key] = [entry];
  });
  return out;
}

/**
 * Create or update the PPPC policy for the given app.
 *
 * Audit trail: every successful save writes a `created` or `updated` entry
 * to the PPPC activity log (with a snapshot for diffing) so the event trail
 * is complete regardless of which caller invoked the API. The log write is
 * best-effort and never blocks the save.
 */
export async function savePppcPolicy(args: SavePppcArgs): Promise<{ id: string }> {
  const {
    existingPolicyId,
    appName,
    bundleId,
    codeRequirement,
    choices,
    identifierType = "bundleID",
    tenantIds,
    allTenants,
    tagIds,
    previousTagIds,
  } = args;

  // Server-side validation step: reject malformed custom TCC keys before they
  // reach the MDM API. Throws PppcValidationError, which the UI surfaces as a
  // clear inline error + toast.
  assertValidPppcChoices(choices);

  const services = buildServicesDict({
    bundleId,
    identifierType,
    codeRequirement,
    choices,
  });

  const request: CreatePolicyRequest = {
    name: buildPppcPolicyName(appName, bundleId, identifierType),
    description: `Privacy Preferences Policy Control for ${appName}`,
    payloads: [
      {
        kind: "profile",
        identifier: PPPC_TCC_IDENTIFIER,
        config: { Services: services },
      },
    ],
    ...(allTenants
      ? { allTenants: true }
      : tenantIds && tenantIds.length > 0
        ? { tenantIds }
        : {}),
  };

  const wasUpdate = !!existingPolicyId;
  let savedId: string;
  if (existingPolicyId) {
    await updatePolicy(existingPolicyId, request);
    savedId = existingPolicyId;
  } else {
    const created = await createPolicy(request);
    savedId = created.id;
  }

  // ---- Audit log write (best-effort) ------------------------------------
  // Building the snapshot mirrors the choices the caller passed (omitting
  // "unset"), so the log entry can be diffed against future entries.
  try {
    const cleanedChoices: Record<string, "allow" | "deny"> = {};
    for (const [k, v] of Object.entries(choices ?? {})) {
      if (v === "allow" || v === "deny") cleanedChoices[k] = v;
    }
    const choiceCount = Object.keys(cleanedChoices).length;
    // Compute deploy-scope delta so each save carries the tag movement that
    // accompanied it, even when the tag reconcile path no-ops (unchanged set).
    const prevTags = Array.isArray(previousTagIds) ? [...previousTagIds] : [];
    const nextTags = Array.isArray(tagIds) ? [...tagIds] : [];
    const prevSet = new Set(prevTags);
    const nextSet = new Set(nextTags);
    const added = nextTags.filter((id) => !prevSet.has(id));
    const removed = prevTags.filter((id) => !nextSet.has(id));
    const reusedExistingPolicy = wasUpdate;
    recordPppcActivity({
      policyId: savedId,
      action: wasUpdate ? "updated" : "created",
      actor: getCurrentUserEmail(),
      bundleId,
      tagIds: nextTags,
      previousTagIds: prevTags,
      summary: `${appName || "PPPC policy"} — ${choiceCount} service${choiceCount === 1 ? "" : "s"} configured`,
      details: {
        identifier: bundleId,
        identifierType,
        serviceCount: choiceCount,
        reusedExistingPolicy,
        previousTagIds: prevTags,
        tagIds: nextTags,
        tagDelta: { added, removed, total: nextTags.length },
      },
      snapshot: {
        appName,
        identifier: bundleId,
        identifierType,
        codeRequirement,
        choices: cleanedChoices,
      },
    });
  } catch {
    // Never let audit-log failures break the save.
  }

  return { id: savedId };
}

/**
 * Delete a PPPC policy by id.
 */
export async function deletePppcPolicy(id: string): Promise<string> {
  return deletePolicy(id);
}
